import { useSyncExternalStore } from 'react';

// A tiny router: the path and query are the state; navigate() pushes, the back button pops.
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());
addEventListener('popstate', notify);

export function navigate(to: string, replace = false) {
  if (to === location.pathname + location.search) return;
  if (replace) history.replaceState(null, '', to);
  else history.pushState(null, '', to);
  notify();
  window.scrollTo?.(0, 0);
}

const snap = () => location.pathname + location.search;
export function useLocation() {
  const href = useSyncExternalStore((l) => (listeners.add(l), () => listeners.delete(l)), snap);
  const u = new URL(href, location.origin);
  // "/boards/" is "/boards": a trailing slash (typed, or from a link elsewhere) never makes a page not found.
  return { path: u.pathname.length > 1 ? u.pathname.replace(/\/+$/, '') || '/' : u.pathname, query: u.searchParams, href };
}

/** "/lists/:id" against "/lists/L-2" -> { id: "L-2" }. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean);
  const s = path.split('/').filter(Boolean);
  if (p.length !== s.length) return null;
  const out: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i].startsWith(':')) {
      // A broken escape (a cut or mistyped link) is no match, so the page says "not here" instead of the app crashing.
      try { out[p[i].slice(1)] = decodeURIComponent(s[i]); } catch { return null; }
    } else if (p[i] !== s[i]) return null;
  }
  return out;
}

/** Change some query keys, keeping the path. null removes a key. */
export function withQuery(patch: Record<string, string | null>, replace = false) {
  const u = new URL(location.href);
  for (const [k, v] of Object.entries(patch)) {
    if (v === null) u.searchParams.delete(k);
    else u.searchParams.set(k, v);
  }
  navigate(u.pathname + u.search, replace);
}
